import { useEffect, type ReactNode } from "react";
import { cn } from "../lib/cn.ts";
import { IconButton } from "./icon-button.tsx";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  width?: number;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  width = 560,
  children,
  footer,
  closeLabel = "Fermer",
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-[500] flex items-center justify-center bg-gray-900/40 p-5 backdrop-blur-[2px] animate-[sf-fade-in_150ms_ease-out]"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sf-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ width, maxWidth: "100%" }}
        className={cn(
          "flex max-h-[90vh] flex-col overflow-hidden rounded-[10px] bg-[var(--sf-surface)]",
          "shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] animate-[sf-modal-in_200ms_cubic-bezier(0.16,1,0.3,1)]",
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--sf-border)] px-5 py-4">
          <div>
            <div
              id="sf-modal-title"
              className="text-base font-semibold tracking-tight text-[var(--sf-text)]"
            >
              {title}
            </div>
            {subtitle && (
              <div className="mt-0.5 text-[12.5px] text-[var(--sf-text-muted)]">
                {subtitle}
              </div>
            )}
          </div>
          <IconButton aria-label={closeLabel} onClick={onClose}>
            <span className="text-lg leading-none">×</span>
          </IconButton>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-[18px]">{children}</div>
        {footer && (
          <div className="border-t border-[var(--sf-border)] px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
